import Dexie, { type EntityTable, type Transaction } from 'dexie';
import type {
  CalibrationProfile,
  EnvironmentSnapshot,
  SessionRecord,
  TrialResult,
} from '../core/types';
import type { MetadataRecord, SettingsRecord, StoredModuleSummary } from './types';

export const DATABASE_NAME = 'scan90-lab';
export const DATABASE_SCHEMA_VERSION = 2;

const versionOneStores = {
  calibrations: '&id, createdAt',
  environments: '&id, capturedAt, calibrationId',
  sessions: '&id, startedAt, completedAt, mode, status, protocolVersion, environmentId',
  trials:
    '&id, sessionId, trialId, moduleId, startedAt, protocolVersion, environmentId, valid, [sessionId+moduleId]',
  settings: '&key, updatedAt',
};

const versionTwoStores = {
  ...versionOneStores,
  sessions: '&id, startedAt, completedAt, mode, status, protocolVersion, environmentId, *moduleIds',
  summaries: '&id, sessionId, moduleId, [sessionId+moduleId]',
  metadata: '&key, updatedAt',
};

async function migrateToVersionTwo(transaction: Transaction): Promise<void> {
  const trialsTable = transaction.table<TrialResult, string>('trials');
  const retainedTrials = await trialsTable.toArray();
  const trialsBySession = new Map<string, TrialResult[]>();
  for (const trial of retainedTrials) {
    const sessionTrials = trialsBySession.get(trial.sessionId) ?? [];
    sessionTrials.push(trial);
    trialsBySession.set(trial.sessionId, sessionTrials);
  }
  for (const sessionTrials of trialsBySession.values()) {
    sessionTrials.sort((left, right) =>
      left.startedAt.localeCompare(right.startedAt) || left.id.localeCompare(right.id));
  }

  await transaction
    .table<SessionRecord, string>('sessions')
    .toCollection()
    .modify((session) => {
      const sessionTrials = trialsBySession.get(session.id) ?? [];
      session.trialIds = sessionTrials.map(({ id }) => id);
      session.moduleIds = [...new Set(sessionTrials.map(({ moduleId }) => moduleId))];
    });
  await trialsTable
    .toCollection()
    .modify((trial) => {
      trial.metadata ??= {};
    });
  await transaction.table<MetadataRecord, string>('metadata').put({
    key: 'schemaMigratedTo',
    value: DATABASE_SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
  });
}

export class Scan90Database extends Dexie {
  calibrations!: EntityTable<CalibrationProfile, 'id'>;
  environments!: EntityTable<EnvironmentSnapshot, 'id'>;
  sessions!: EntityTable<SessionRecord, 'id'>;
  trials!: EntityTable<TrialResult, 'id'>;
  summaries!: EntityTable<StoredModuleSummary, 'id'>;
  settings!: EntityTable<SettingsRecord, 'key'>;
  metadata!: EntityTable<MetadataRecord, 'key'>;

  constructor(name = DATABASE_NAME) {
    super(name);
    this.version(1).stores(versionOneStores);
    this.version(DATABASE_SCHEMA_VERSION).stores(versionTwoStores).upgrade(migrateToVersionTwo);
  }
}

export const scan90Database = new Scan90Database();
